// THE NETWORK GRAPH'S LAYOUT (BUILD-BRIEF-R5 §6.3) — deterministic, three-column, no library,
// no force simulation (P7). Pure: no React, no DOM. `NetGraph.tsx` is the only caller.
//
// The COLUMN a node belongs to is decided by the server (`NetworkNode.column`, §6.1: 0 =
// payer/referrer/funder/accreditor counterparts, 1 = subjects, 2 = umbrella/partner/vendor/
// consultant/auditor counterparts) — this module never re-derives it. What this module owns is
// everything downstream of that: the row order within a column, the y a row sits at, the x a
// column sits at (from the sheet's MEASURED width, never a scaled viewBox — 12px text must stay
// 12 real pixels), which nodes survive when a node is focused, and the width of every edge.
import type { LinkBasis, NetworkEdge, NetworkNode } from '../../../shared/network.v1.js';

export interface LayoutNode {
  id: string;
  x: number;
  y: number;
  column: 0 | 1 | 2;
}

export interface LayoutEdge {
  id: string;
  /** `1 + 5 × (quantity / max within the same basis)`, clamped to `[1, 6]`; `1.5` unquantified. */
  width: number;
}

export interface LayoutResult {
  nodes: LayoutNode[];
  edges: LayoutEdge[];
  /** The most rows any column holds — `NetGraph` sets its SVG height to `rows × 34` from this. */
  rows: number;
}

const ROW_HEIGHT = 34;
const TOP_MARGIN = 20;
/** Left/right breathing room so a square or a label is never clipped by the sheet's edge. */
const SIDE_MARGIN = 96;

/** Every edge touching `id`, in either direction. */
function incidentQuantity(id: string, edges: readonly NetworkEdge[]): number {
  let sum = 0;
  for (const e of edges) {
    if (e.from !== id && e.to !== id) continue;
    if (typeof e.quantity === 'number') sum += e.quantity;
  }
  return sum;
}

/** `null` sorts after every real canton (unassigned is not "before Argovie"). */
function cantonKey(canton: string | null): string {
  return canton ?? '￿';
}

/**
 * Rows within one column: total incident quantity within its basis desc, then canton, then
 * name — and finally `id`, so two nodes tied on all three still resolve the same way every time
 * (the "same input → same output" property the test pins).
 */
function sortColumn(nodes: NetworkNode[], edges: readonly NetworkEdge[]): NetworkNode[] {
  const qty = new Map<string, number>();
  for (const n of nodes) qty.set(n.id, incidentQuantity(n.id, edges));
  return [...nodes].sort((a, b) => {
    const q = (qty.get(b.id) ?? 0) - (qty.get(a.id) ?? 0);
    if (q !== 0) return q;
    const c = cantonKey(a.canton).localeCompare(cantonKey(b.canton));
    if (c !== 0) return c;
    const n = a.name.localeCompare(b.name);
    if (n !== 0) return n;
    return a.id.localeCompare(b.id);
  });
}

/** The nodes a focused node keeps around it: itself alone in its own column, and — in the other
 *  two — only the nodes an edge actually connects it to. */
function visibleFor(nodes: NetworkNode[], edges: readonly NetworkEdge[], focus: string): NetworkNode[] {
  const focusNode = nodes.find((n) => n.id === focus);
  if (!focusNode) return nodes;
  const neighbours = new Set<string>();
  for (const e of edges) {
    if (e.from === focus) neighbours.add(e.to);
    if (e.to === focus) neighbours.add(e.from);
  }
  return nodes.filter((n) => {
    if (n.id === focus) return true;
    if (n.column === focusNode.column) return false;
    return neighbours.has(n.id);
  });
}

function xOf(column: 0 | 1 | 2, width: number): number {
  const usable = Math.max(width - 2 * SIDE_MARGIN, 0);
  return SIDE_MARGIN + (usable * column) / 2;
}

/**
 * Edge width, basis by basis: `1 + 5 × (quantity / max within the same basis)`, clamped to
 * `[1, 6]`; an unquantified edge (no basis, no quantity) is `1.5`. The max is taken over every
 * edge this call was handed, not just the ones a focus left visible — an edge's line should not
 * suddenly thicken because the busier edges beside it were focused away.
 */
function edgeWidths(edges: readonly NetworkEdge[]): Map<string, number> {
  const maxByBasis = new Map<LinkBasis, number>();
  for (const e of edges) {
    if (e.basis == null || e.quantity == null) continue;
    const cur = maxByBasis.get(e.basis) ?? 0;
    if (e.quantity > cur) maxByBasis.set(e.basis, e.quantity);
  }
  const out = new Map<string, number>();
  for (const e of edges) {
    let w: number;
    if (e.basis != null && e.quantity != null) {
      const max = maxByBasis.get(e.basis) || e.quantity || 1;
      w = 1 + 5 * (e.quantity / max);
    } else {
      w = 1.5;
    }
    out.set(e.id, Math.max(1, Math.min(6, w)));
  }
  return out;
}

export function layoutNetwork(
  nodes: readonly NetworkNode[],
  edges: readonly NetworkEdge[],
  opts: { focus?: string | null; width?: number } = {},
): LayoutResult {
  const width = opts.width && opts.width > 0 ? opts.width : 720;
  const focus = opts.focus ?? null;

  let visible = [...nodes];
  if (focus) visible = visibleFor(visible, edges, focus);

  const byColumn: Record<0 | 1 | 2, NetworkNode[]> = { 0: [], 1: [], 2: [] };
  for (const n of visible) byColumn[n.column].push(n);

  const widths = edgeWidths(edges);
  const layoutEdges: LayoutEdge[] = edges.map((e) => ({ id: e.id, width: widths.get(e.id) ?? 1.5 }));

  const layoutNodes: LayoutNode[] = [];
  let rows = 0;
  for (const column of [0, 1, 2] as const) {
    const sorted = sortColumn(byColumn[column], edges);
    rows = Math.max(rows, sorted.length);
    sorted.forEach((n, row) => {
      layoutNodes.push({ id: n.id, column, x: xOf(column, width), y: TOP_MARGIN + row * ROW_HEIGHT });
    });
  }

  return { nodes: layoutNodes, edges: layoutEdges, rows };
}
